// V1 Adapter — bridges V2 import names to V1 equivalents.
// This module is the ONLY file that differs between V1 and V2.
// All other files in new_request/ are copies from V2 with import paths rewritten.

import React, { useEffect, useRef } from 'react';
import { authenticatedHeadersHash } from '../../javascript/v2_authentication';
import vlog from '../../javascript/vlog';
import { fetchWithTokenRefresh } from '../../javascript/fetchWithTokenRefresh';

export { authenticatedHeadersHash };

// V1's vlog is equivalent to V2's qlog
export const qlog = vlog;

// Hardcoded V2 API routes (V2 uses globalThis.Qwoted.ROUTES)
const V2_URL = window.V2_URL || '';
const SUPABASE_URL = window.V2_SUPABASE_API_EXTERNAL_URL || '';

export const V2_ROUTES = {
  frontend: {
    my_requests: '/reporter_requests',
  },
  id: {
    api_media_outlets: `${V2_URL}/id/api/media_outlets`,
  },
  interact: {
    api_requests: `${V2_URL}/interact/api/requests`,
    api_request: `${V2_URL}/interact/api/requests/:id`,
  },
  rest: {
    media_outlets: `${SUPABASE_URL}/rest/v1/media_outlets`,
  },
};

// Hardcoded validation limits (matches V2 Interact::Request model constraints)
export const REQUEST_VALIDATION_LIMITS = {
  title: { min: 3, max: 90 },
  details: { min: 10, max: 3000 },
  equipment_notes: { max: 500 },
};

// V1 Feature Flags
export const V1_FEATURES = {
  SKIP_REVIEW_AND_THANKS_PAGES: true,
};

// Supabase client accessor (V2 uses globalThis.Qwoted.supabaseClient)
export const getSupabaseClient = () => {
  if (!window.supabaseClient && window.initializeSupabaseClient) {
    window.initializeSupabaseClient();
  }
  return window.supabaseClient;
};

// =============================================================================
// qapi - Centralized API interface (mimics V2's views_shared/app/javascript/api)
// =============================================================================

/**
 * Media outlets API
 */
const mediaOutletsApi = {
  /**
   * Create a new media outlet via Rails API
   */
  async create(outletData) {
    const response = await fetchWithTokenRefresh(V2_ROUTES.id.api_media_outlets, {
      method: 'POST',
      headers: {
        ...authenticatedHeadersHash(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          attributes: {
            name: outletData.name,
            domain: outletData.domain,
            description: outletData.description,
          },
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errors = errorData.errors || [];
      const errorMessage = errors.length > 0 ? errors[0].detail : 'Failed to create outlet';
      throw new Error(errorMessage);
    }

    const json = await response.json();
    if (!json.data) {
      throw new Error('Invalid response format from server');
    }

    const { data } = json;
    return {
      id: data.id,
      ...data.attributes,
    };
  },

  /**
   * Load media outlets from Supabase PostgREST with search
   * Returns array of { label, value, domain, prestige, imageUrl }
   */
  async getAll({
    id = null, search = '', signal = null, limit = 20,
  } = {}) {
    if (search && search.trim().length > 100) {
      throw new Error('Search term must be 100 characters or less');
    }

    const params = new URLSearchParams({
      select: 'id,name,domain,prestige,visibility,profile_image_url',
      order: 'prestige.desc,name.asc',
      limit: limit.toString(),
    });

    if (id) params.set('id', `eq.${id}`);
    if (search) params.set('name', `ilike.%${search.trim()}%`);

    const url = `${V2_ROUTES.rest.media_outlets}?${params.toString()}`;

    const response = await fetchWithTokenRefresh(url, {
      method: 'GET',
      headers: { ...authenticatedHeadersHash() },
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.statusText || 'Failed to load media outlets');
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      throw new Error('No data returned from server');
    }

    return data.map((e) => ({
      label: e.name,
      value: e.id,
      domain: e.domain,
      prestige: e.prestige,
      imageUrl: e.profile_image_url,
    }));
  },
};

/**
 * Source requests API
 */
const requestsApi = {
  /**
   * Create a new source request (as draft)
   */
  async create(requestData) {
    const {
      post,
      reporter_profile_id: reporterProfileId,
      reporter_profile_migrate_id: reporterProfileMigrateId,
      ...attributes
    } = requestData;

    const data = {
      attributes,
    };

    if (post !== undefined) {
      data.post = post;
    }

    if (reporterProfileId !== undefined) {
      data.reporter_profile_id = reporterProfileId;
    }

    if (reporterProfileMigrateId !== undefined) {
      data.reporter_profile_migrate_id = reporterProfileMigrateId;
    }

    if (post !== undefined) {
      data.post = post;
    }

    const response = await fetchWithTokenRefresh(V2_ROUTES.interact.api_requests, {
      method: 'POST',
      headers: {
        ...authenticatedHeadersHash(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Failed to create request: ${response.status} ${response.statusText}${
          errorData.error ? ` - ${errorData.error}` : ''
        }`,
      );
    }

    const result = await response.json();

    // Handle JSON:API format: { data: { id, attributes } }
    if (result.data && result.data.attributes) {
      return {
        id: result.data.id,
        ...result.data.attributes,
      };
    }

    return result;
  },

  /**
   * Fetch an existing source request by V1 migrate_id (for edit mode)
   */
  async get(migrateId) {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('migrate_id', migrateId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch request: ${error.message}`);
    }

    if (!data) {
      throw new Error(`No V2 request found with migrate_id: ${migrateId}`);
    }

    return data;
  },

  /**
   * Update an existing source request
   */
  async update(id, requestData, abortSignal) {
    // Extract the 'post' flag and 'idempotency_key' if present
    // eslint-disable-next-line camelcase
    const { post, idempotency_key, ...attributes } = requestData;

    const data = {
      attributes,
    };

    // If post flag is present, add it at the data level for the backend controller
    if (post !== undefined) {
      data.post = post;
    }

    const payload = {
      method: 'PATCH',
      headers: {
        ...authenticatedHeadersHash(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    };

    if (abortSignal) {
      payload.signal = abortSignal;
    }

    const url = V2_ROUTES.interact.api_request.replace(':id', id);
    const response = await fetchWithTokenRefresh(url, payload);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Failed to update request: ${response.status} ${response.statusText}${
          errorData.error ? ` - ${errorData.error}` : ''
        }`,
      );
    }

    const result = await response.json();

    // Handle JSON:API format: { data: { id, attributes } }
    if (result.data && result.data.attributes) {
      return {
        id: result.data.id,
        ...result.data.attributes,
      };
    }

    return result;
  },
};

/**
 * Centralized API object (mimics V2's qapi from views_shared)
 */
export const qapi = {
  mediaOutlets: mediaOutletsApi,
  requests: requestsApi,
};

// =============================================================================
// useAutoResizeTextarea - Auto-resize textarea hook
// =============================================================================

/**
 * Hook to auto-resize textarea based on content
 * @param {string} content - Current textarea content
 * @param {number} minHeight - Minimum height in pixels
 * @returns {Object} React ref to attach to textarea
 */
export function useAutoResizeTextarea(content, minHeight = 100) {
  const textareaRef = useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    const newHeight = Math.max(minHeight, textarea.scrollHeight);
    textarea.style.height = `${newHeight}px`;
  }, [content, minHeight]);

  return textareaRef;
}

// =============================================================================
// Case conversion utilities
// =============================================================================

/**
 * Convert string from snake_case to camelCase
 */
function camelCase(str) {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Convert string from camelCase to snake_case
 */
function snakeCase(str) {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
}

/**
 * Deep convert object keys from snake_case to camelCase
 */
export function deepCamelizeKeys(obj) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(deepCamelizeKeys);
  if (typeof obj !== 'object') return obj;

  const result = {};
  Object.keys(obj).forEach((key) => {
    result[camelCase(key)] = deepCamelizeKeys(obj[key]);
  });
  return result;
}

/**
 * Deep convert object keys from camelCase to snake_case
 */
export function deepSnakeKeys(obj) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(deepSnakeKeys);
  if (typeof obj !== 'object') return obj;

  const result = {};
  Object.keys(obj).forEach((key) => {
    result[snakeCase(key)] = deepSnakeKeys(obj[key]);
  });
  return result;
}

// =============================================================================
// V1-specific UI Components
// =============================================================================

/**
 * Redirect spinner shown during page transition
 * Used when V1 feature flag bypasses React pages and redirects to Rails
 */
export function RedirectSpinner() {
  return (
    <div className="new-request-body d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
      <div className="text-center">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Redirecting...</span>
        </div>
        <p className="text-muted">Redirecting to thanks page...</p>
      </div>
    </div>
  );
}
